import { CheckSquare, Clock, AlertTriangle, TrendingUp } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { useRealtime } from '../../context/RealtimeContext'
import type { EmployeeSection } from './EmployeeLayout'

const GREETINGS = [
  'Make today count.',
  'Let\'s get things done.',
  'You\'re doing great — keep going.',
  'Stay focused, stay sharp.',
  'One task at a time.',
]

interface Props { onNav: (s: EmployeeSection) => void }

export default function EmployeeHome({ onNav }: Props) {
  const { currentUser } = useAuth()
  const { tasksFor } = useRealtime()
  const myTasks = tasksFor(currentUser?.name ?? '')

  const active  = myTasks.filter(t => t.status === 'in-progress')
  const todo    = myTasks.filter(t => t.status === 'todo')
  const review  = myTasks.filter(t => t.status === 'review')
  const done    = myTasks.filter(t => t.status === 'done')
  const overdue = myTasks.filter(t => t.status !== 'done' && new Date(t.dueDate) < new Date())

  const greeting = GREETINGS[new Date().getDay() % GREETINGS.length]

  const stats = [
    { label: 'To Do',      value: todo.length,    icon: Clock,          color: '#ffc600' },
    { label: 'In Progress', value: active.length,  icon: TrendingUp,     color: '#e8a000' },
    { label: 'In Review',  value: review.length,  icon: CheckSquare,    color: '#ff8c00' },
    { label: 'Overdue',    value: overdue.length, icon: AlertTriangle,  color: overdue.length > 0 ? '#cc3300' : '#555' },
  ]

  function priorityColor(p: string) {
    switch (p) {
      case 'urgent': return '#cc3300'
      case 'high':   return '#ff8c00'
      case 'medium': return '#ffc600'
      default:       return '#555'
    }
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>
          Good morning, {currentUser?.name} 👋
        </h1>
        <p className="text-sm mt-0.5" style={{ color: '#a0a0a0' }}>{greeting}</p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="rounded-xl p-4" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.12)' }}>
            <div className="flex items-center gap-2 mb-2">
              <Icon size={15} style={{ color }} />
              <p className="text-xs" style={{ color: '#a0a0a0' }}>{label}</p>
            </div>
            <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>{value}</p>
          </div>
        ))}
      </div>

      {/* Active tasks */}
      <div className="rounded-xl p-5" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.12)' }}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-bold text-white">Active Tasks</h2>
          <button onClick={() => onNav('tasks')} className="text-xs" style={{ color: '#ffc600' }}>View all →</button>
        </div>
        {active.length === 0 ? (
          <p className="text-sm text-center py-4" style={{ color: '#333' }}>No tasks in progress — pick one from your To Do list.</p>
        ) : (
          <div className="space-y-3">
            {active.slice(0, 4).map(t => (
              <div key={t.id} className="flex items-center justify-between px-3 py-2.5 rounded-lg"
                style={{ background: '#1a1a1a', border: '1px solid rgba(255,198,0,0.06)' }}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{t.title}</p>
                  <p className="text-xs mt-0.5" style={{ color: '#555' }}>
                    Due {new Date(t.dueDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                  </p>
                </div>
                <span className="ml-3 text-xs px-2 py-0.5 rounded capitalize flex-shrink-0"
                  style={{ background: `${priorityColor(t.priority)}18`, color: priorityColor(t.priority) }}>
                  {t.priority}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Completion progress */}
      {myTasks.length > 0 && (
        <div className="rounded-xl p-5" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.12)' }}>
          <h2 className="text-sm font-bold text-white mb-3">Overall Progress</h2>
          <div className="flex items-center gap-3 mb-1">
            <div className="flex-1 rounded-full overflow-hidden" style={{ height: 6, background: '#1a1a1a' }}>
              <div className="h-full rounded-full transition-all"
                style={{ width: `${Math.round((done.length / myTasks.length) * 100)}%`, background: '#ffc600' }} />
            </div>
            <span className="text-xs font-semibold text-white">
              {Math.round((done.length / myTasks.length) * 100)}%
            </span>
          </div>
          <p className="text-xs" style={{ color: '#555' }}>{done.length} of {myTasks.length} tasks completed</p>
        </div>
      )}
    </div>
  )
}
